import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Modal, Field, TextArea, Select, Button, StatusBadge } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, Trash, User, Buildings, CaretRight, Kanban, ListDashes, CalendarBlank, BellRinging, PencilSimple, DotsSixVertical } from "@phosphor-icons/react";

const emptyForm = { title: "", description: "", worker_id: "", customer_id: "", priority: "medium", status: "todo", due_date: "", due_time: "", reminders: [] };
const REMINDER_OPTS = [
  { h: 1, label: "1 hour" },
  { h: 3, label: "3 hours" },
  { h: 24, label: "1 day" },
  { h: 48, label: "2 days" },
  { h: 168, label: "1 week" },
];
const COLUMNS = [
  { k: "todo", label: "To Do" },
  { k: "in_progress", label: "In Progress" },
  { k: "done", label: "Done" },
];
const NEXT = { todo: "in_progress", in_progress: "done", done: "todo" };
const dotColor = { high: "#B91C1C", medium: "#F49B1C", low: "#94A3B8" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [view, setView] = useState("board");

  const load = useCallback(() => api.get("/tasks").then(({ data }) => setTasks(data)), []);
  useEffect(() => {
    load();
    api.get("/workers").then(({ data }) => setWorkers(data));
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title || "",
      description: t.description || "",
      worker_id: t.worker_id || "",
      customer_id: t.customer_id || "",
      priority: t.priority || "medium",
      status: t.status || "todo",
      due_date: t.due_date ? String(t.due_date).slice(0, 10) : "",
      due_time: t.due_time || "",
      reminders: Array.isArray(t.reminders) ? t.reminders : [],
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      worker_id: form.worker_id || null,
      customer_id: form.customer_id || null,
      due_date: form.due_date || null,
      due_time: form.due_time || null,
      reminders: form.reminders,
    };
    try {
      if (editing) {
        await api.put(`/tasks/${editing.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch {
      toast.error(editing ? "Failed to update task" : "Failed to create task");
    }
  };

  const advance = async (t) => {
    await api.put(`/tasks/${t.id}`, { status: NEXT[t.status] });
    load();
  };

  const setStatus = async (t, status) => {
    if (t.status === status) return;
    await api.put(`/tasks/${t.id}`, { status });
    load();
  };

  const onDrop = (colKey) => {
    setDragOver(null);
    const t = tasks.find((x) => x.id === dragId);
    setDragId(null);
    if (t) setStatus(t, colKey);
  };

  const remove = async (id) => {
    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    load();
  };

  const TaskMeta = ({ t }) => (
    <div className="flex flex-wrap gap-2 mt-2 text-xs font-mono text-slate-400">
      <span className="flex items-center gap-1"><User size={12} weight="fill" /> {t.worker_name || "Unassigned"}</span>
      {t.customer_name && <span className="flex items-center gap-1"><Buildings size={12} weight="fill" /> {t.customer_name}</span>}
      {t.due_date && <span className="flex items-center gap-1"><CalendarBlank size={12} weight="fill" /> {new Date(t.due_date).toLocaleDateString()}{t.due_time ? ` ${t.due_time}` : ""}</span>}
      {Array.isArray(t.reminders) && t.reminders.length > 0 && (
        <span className="flex items-center gap-1 text-[#3730A3]"><BellRinging size={12} weight="fill" /> {t.reminders.length}</span>
      )}
    </div>
  );

  const toggleReminder = (h) =>
    setForm((f) => ({ ...f, reminders: f.reminders.includes(h) ? f.reminders.filter((x) => x !== h) : [...f.reminders, h] }));

  // timeline: tasks with due_date sorted ascending, then those without
  const withDate = tasks.filter((t) => t.due_date).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  const noDate = tasks.filter((t) => !t.due_date);
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <PageHeader
        title="Tasks & Jobs"
        subtitle="Assign work and hold the crew accountable"
        action={
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-slate-300 bg-white p-0.5">
              <button data-testid="view-board" onClick={() => setView("board")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === "board" ? "bg-[#16205A] text-white" : "text-slate-500"}`}>
                <Kanban size={15} weight="bold" /> Board
              </button>
              <button data-testid="view-timeline" onClick={() => setView("timeline")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === "timeline" ? "bg-[#16205A] text-white" : "text-slate-500"}`}>
                <ListDashes size={15} weight="bold" /> Timeline
              </button>
            </div>
            <Button data-testid="add-task-button" onClick={openCreate}>
              <Plus size={16} weight="bold" /> New Task
            </Button>
          </div>
        }
      />

      <div className="p-8">
        {view === "board" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {COLUMNS.map((col) => {
              const items = tasks.filter((t) => t.status === col.k);
              return (
                <div
                  key={col.k}
                  data-testid={`column-${col.k}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(col.k); }}
                  onDragLeave={() => setDragOver((c) => (c === col.k ? null : c))}
                  onDrop={() => onDrop(col.k)}
                  className={`card-soft overflow-hidden fade-up transition-all ${dragOver === col.k ? "ring-2 ring-[#F49B1C] ring-offset-2" : ""}`}
                >
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-heading font-extrabold text-base text-[#16205A]">{col.label}</h3>
                    <span className="font-mono text-xs bg-[#16205A] text-white px-2 py-0.5 rounded-full">{items.length}</span>
                  </div>
                  <div className="p-3 space-y-3 min-h-[140px]">
                    {items.map((t) => (
                      <div
                        key={t.id}
                        data-testid={`task-card-${t.id}`}
                        draggable
                        onDragStart={() => setDragId(t.id)}
                        onDragEnd={() => { setDragId(null); setDragOver(null); }}
                        className={`rounded-xl border border-slate-200 p-3.5 bg-white hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${dragId === t.id ? "opacity-50" : ""}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-sm text-slate-800 flex items-start gap-1">
                            <DotsSixVertical size={16} weight="bold" className="text-slate-300 mt-0.5 shrink-0" />
                            {t.title}
                          </p>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button data-testid={`edit-task-${t.id}`} onClick={() => openEdit(t)} className="text-slate-300 hover:text-[#16205A] transition-colors">
                              <PencilSimple size={14} weight="bold" />
                            </button>
                            <button data-testid={`delete-task-${t.id}`} onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                              <Trash size={14} weight="bold" />
                            </button>
                          </div>
                        </div>
                        {t.description && <p className="text-xs text-slate-500 mt-1">{t.description}</p>}
                        <TaskMeta t={t} />
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                          <StatusBadge status={t.priority} />
                          <button
                            data-testid={`advance-task-${t.id}`}
                            onClick={() => advance(t)}
                            className="flex items-center gap-1 font-semibold text-[10px] uppercase rounded-md border border-slate-300 px-2.5 py-1 hover:bg-[#F49B1C] hover:text-white hover:border-[#F49B1C] transition-colors"
                          >
                            {t.status === "done" ? "Reopen" : "Advance"} <CaretRight size={12} weight="bold" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && <p className="text-xs text-slate-300 text-center py-8">Drop here</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card-soft p-8 fade-up max-w-3xl">
            {withDate.length === 0 && noDate.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No tasks yet.</p>}
            <div className="relative pl-8">
              {withDate.length > 0 && <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200" />}
              {withDate.map((t) => {
                const overdue = t.status !== "done" && String(t.due_date).slice(0, 10) < todayStr;
                return (
                  <div key={t.id} data-testid={`timeline-task-${t.id}`} className="relative mb-6 last:mb-0">
                    <div
                      className="absolute -left-8 top-1 w-6 h-6 rounded-full border-4 border-white shadow flex items-center justify-center"
                      style={{ backgroundColor: t.status === "done" ? "#22C55E" : dotColor[t.priority] }}
                    />
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-mono text-xs font-semibold ${overdue ? "text-[#B91C1C]" : "text-slate-500"}`}>
                        {new Date(t.due_date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                      {overdue && <StatusBadge status="overdue" label="OVERDUE" />}
                    </div>
                    <div className="rounded-xl border border-slate-200 p-4 bg-white flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-sm text-slate-800">{t.title}</p>
                        {t.description && <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>}
                        <TaskMeta t={t} />
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`edit-task-${t.id}`} onClick={() => openEdit(t)} className="text-slate-300 hover:text-[#16205A] transition-colors"><PencilSimple size={15} weight="bold" /></button>
                          <button data-testid={`delete-task-${t.id}`} onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash size={15} weight="bold" /></button>
                        </div>
                        <StatusBadge status={t.status} />
                        <button
                          data-testid={`advance-task-${t.id}`}
                          onClick={() => advance(t)}
                          className="flex items-center gap-1 font-semibold text-[10px] uppercase rounded-md border border-slate-300 px-2.5 py-1 hover:bg-[#F49B1C] hover:text-white hover:border-[#F49B1C] transition-colors"
                        >
                          {t.status === "done" ? "Reopen" : "Advance"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {noDate.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-100">
                <h4 className="font-heading font-bold text-xs uppercase tracking-wide text-slate-400 mb-3">No due date</h4>
                <div className="space-y-2">
                  {noDate.map((t) => (
                    <div key={t.id} data-testid={`timeline-task-${t.id}`} className="rounded-xl border border-slate-200 p-4 bg-white flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-sm text-slate-800">{t.title}</p>
                        <TaskMeta t={t} />
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`edit-task-${t.id}`} onClick={() => openEdit(t)} className="text-slate-300 hover:text-[#16205A] transition-colors"><PencilSimple size={15} weight="bold" /></button>
                          <button data-testid={`delete-task-${t.id}`} onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash size={15} weight="bold" /></button>
                        </div>
                        <StatusBadge status={t.status} />
                        <button data-testid={`advance-task-${t.id}`} onClick={() => advance(t)} className="flex items-center gap-1 font-semibold text-[10px] uppercase rounded-md border border-slate-300 px-2.5 py-1 hover:bg-[#F49B1C] hover:text-white hover:border-[#F49B1C] transition-colors">
                          {t.status === "done" ? "Reopen" : "Advance"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); }} title={editing ? "Edit Task" : "New Task"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Title *" data-testid="task-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <TextArea label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Assign Worker" data-testid="task-worker-select" value={form.worker_id} onChange={(e) => setForm({ ...form, worker_id: e.target.value })}>
              <option value="">Unassigned</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Select>
            <Select label="Customer / Job" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
              <option value="">None</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Priority" data-testid="task-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </Select>
            {editing ? (
              <Select label="Status" data-testid="task-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </Select>
            ) : (
              <Field label="Due Date" type="date" data-testid="task-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            )}
          </div>
          {editing && (
            <Field label="Due Date" type="date" data-testid="task-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          )}
          <div>
            <label className="font-semibold text-xs uppercase tracking-wide text-slate-500 block mb-1.5">Due Time (optional)</label>
            <input type="time" data-testid="task-due-time" value={form.due_time} onChange={(e) => setForm({ ...form, due_time: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F49B1C]/60 focus:border-[#F49B1C]" />
          </div>
          <div>
            <label className="font-semibold text-xs uppercase tracking-wide text-slate-500 block mb-1.5">Reminders (before delivery)</label>
            <div className="flex flex-wrap gap-2">
              {REMINDER_OPTS.map((o) => {
                const on = form.reminders.includes(o.h);
                return (
                  <button
                    key={o.h}
                    type="button"
                    data-testid={`task-reminder-${o.h}`}
                    onClick={() => toggleReminder(o.h)}
                    className={`text-xs font-semibold rounded-lg border px-3 py-1.5 transition-colors ${on ? "bg-[#16205A] text-white border-[#16205A]" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Get reminded on the dashboard this long before the task is due.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
            <Button type="submit" data-testid="save-task-button">{editing ? "Save Changes" : "Create"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
